import { api } from './axios';

export type FeeFrequency = 'MONTHLY' | 'ONE_TIME' | 'PER_TERM';
export type PaymentMethod = 'CASH' | 'BANK_TRANSFER' | 'ONLINE_TRANSFER';

export interface FeeType {
  id: number;
  name: string;
  frequency: FeeFrequency;
  active: boolean;
  incomeAccountId: number;
  incomeAccountCode: string | null;
  incomeAccountName: string | null;
}

export interface FeeTypeInput {
  name: string;
  frequency: FeeFrequency;
  incomeAccountId: number;
}

export interface FeeStructure {
  id: number;
  academicYearId: number;
  classSectionId: number;
  className: string | null;
  sectionName: string | null;
  feeTypeId: number;
  feeTypeName: string | null;
  frequency: FeeFrequency | null;
  amount: string;
}

export interface FeeStructureInput {
  academicYearId: number;
  classSectionId: number;
  feeTypeId: number;
  amount: string;
}

export interface EnrollmentFeeStructureRow {
  feeTypeId: number;
  feeTypeName: string | null;
  frequency: FeeFrequency | null;
  amount: string;
  discountAmount: string;
  netAmount: string;
}

export interface Discount {
  id: number;
  enrollmentId: number;
  feeTypeId: number;
  feeTypeName: string | null;
  discountTypeId: number;
  discountTypeName: string | null;
  percentage: boolean;
  value: string;
  remarks: string | null;
}

export interface DiscountInput {
  feeTypeId: number;
  discountTypeId: number;
  percentage: boolean;
  value: string;
  remarks?: string;
}

export interface AddDiscountResult {
  discount: Discount;
  invoicesUpdated: number;
}

export interface Invoice {
  id: number;
  enrollmentId: number;
  feeTypeId: number;
  feeTypeName: string | null;
  periodMonth: string | null;
  grossAmount: string;
  discountAmount: string;
  netAmount: string;
  paidAmount: string;
  dueAmount: string;
  waivedAmount: string;
  waiverReason: string | null;
  manual: boolean;
  manualReason: string | null;
  reversed: boolean;
  reversalReason: string | null;
}

export interface WaiveInput {
  amount: string;
  reason: string;
}

export interface ReceiptLine {
  invoiceId: number;
  label: string;
  amountPaid: string;
}

export interface Receipt {
  id: number;
  receiptNumber: number;
  enrollmentId: number;
  paymentDate: string;
  totalAmount: string;
  paymentMethod: PaymentMethod;
  proofReference: string | null;
  bankAccountId: number | null;
  bankAccountName: string | null;
  advanceAmount: string;
  reversed: boolean;
  reversalReason: string | null;
  reversalReceiptId: number | null;
  lines: ReceiptLine[];
}

export interface CollectInput {
  enrollmentId: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  proofReference?: string;
  bankAccountId: number;
  totalAmount: string;
  advanceAmount?: string;
  lines: { invoiceId: number; amountPaid: string }[];
}

export interface StudentLedger {
  enrollmentId: number;
  studentId: number;
  firstName: string;
  lastName: string;
  registrationNumber: string;
  className: string | null;
  sectionName: string | null;
  billed: string;
  paid: string;
  outstanding: string;
  advanceBalance: string;
  invoices: Invoice[];
  receipts: Receipt[];
  discounts: Discount[];
}

export interface DefaulterRow {
  enrollmentId: number;
  studentId: number;
  registrationNumber: string;
  firstName: string;
  lastName: string;
  className: string;
  sectionName: string;
  monthsDue: number;
  outstanding: string;
  primaryGuardianPhone: string | null;
}

export const feeApi = {
  // types
  listTypes: (activeOnly = true) =>
    api.get<FeeType[]>('/fee/types', { params: { activeOnly } }).then((r) => r.data),
  createType: (body: FeeTypeInput) => api.post<FeeType>('/fee/types', body).then((r) => r.data),
  updateType: (id: number, body: FeeTypeInput) => api.put<FeeType>(`/fee/types/${id}`, body).then((r) => r.data),
  toggleType: (id: number, value: boolean) =>
    api.patch<FeeType>(`/fee/types/${id}/active`, null, { params: { value } }).then((r) => r.data),

  // structures
  listStructures: (academicYearId: number, classSectionId?: number) =>
    api.get<FeeStructure[]>('/fee/structures', { params: { academicYearId, classSectionId } }).then((r) => r.data),
  upsertStructure: (body: FeeStructureInput) =>
    api.post<FeeStructure>('/fee/structures', body).then((r) => r.data),
  enrollmentStructure: (enrollmentId: number) =>
    api.get<EnrollmentFeeStructureRow[]>(`/fee/structures/enrollment/${enrollmentId}`).then((r) => r.data),
  deleteStructure: (id: number) => api.delete<void>(`/fee/structures/${id}`).then((r) => r.data),

  // discounts
  listDiscounts: (enrollmentId: number) =>
    api.get<Discount[]>(`/fee/discounts/${enrollmentId}`).then((r) => r.data),
  addDiscount: (enrollmentId: number, body: DiscountInput) =>
    api.post<AddDiscountResult>(`/fee/discounts/${enrollmentId}`, body).then((r) => r.data),
  removeDiscount: (id: number) => api.delete<void>(`/fee/discounts/${id}`).then((r) => r.data),

  // invoices
  addManualInvoice: (body: {
    enrollmentId: number;
    feeTypeId: number;
    periodMonth?: string;
    amount: string;
    reason: string;
    allowDuplicate?: boolean;
  }) => api.post<Invoice>('/fee/invoices/manual', body).then((r) => r.data),
  generateMonthly: (body: { classSectionId: number; periodMonth: string }) =>
    api
      .post<{ enrollmentsTouched: number; invoicesCreated: number; invoicesSkipped: number }>(
        '/fee/invoices/generate-monthly',
        body,
      )
      .then((r) => r.data),
  previewMonthly: (body: { classSectionId: number; periodMonth: string }) =>
    api
      .post<{ activeEnrollments: number; monthlyFeeTypes: number; wouldCreate: number; alreadyExists: number }>(
        '/fee/invoices/generate-monthly/preview',
        body,
      )
      .then((r) => r.data),
  reverseInvoice: (id: number, reason: string) =>
    api.post<Invoice>(`/fee/invoices/${id}/reverse`, { reason }).then((r) => r.data),
  waiveInvoice: (id: number, body: WaiveInput) =>
    api.post<Invoice>(`/fee/invoices/${id}/waive`, body).then((r) => r.data),

  // receipts
  collect: (body: CollectInput) => api.post<Receipt>('/fee/receipts/collect', body).then((r) => r.data),
  reverseReceipt: (id: number, reason: string) =>
    api.post<Receipt>(`/fee/receipts/${id}/reverse`, { reason }).then((r) => r.data),
  getReceipt: (id: number) => api.get<Receipt>(`/fee/receipts/${id}`).then((r) => r.data),

  // ledger + defaulters
  ledger: (enrollmentId: number) => api.get<StudentLedger>(`/fee/ledger/${enrollmentId}`).then((r) => r.data),
  defaulters: (params: {
    academicYearId?: number;
    classSectionId?: number;
    monthsThreshold?: number;
    amountThreshold?: string;
  }) => api.get<DefaulterRow[]>('/fee/defaulters', { params }).then((r) => r.data),
};
